import React, { useState, useEffect, useMemo } from "react";
import { Form, Row, Col, Button, Badge, Alert } from "react-bootstrap";
import { formatARS } from "../helpers/formatearDinero";

// ⭐️ NUEVO HELPER: Función para redondear a dos decimales y asegurar precisión
const roundToTwoDecimals = (num) => {
    // 1. Convertir a número si es string
    const number = typeof num === 'string' ? Number(num.replace(/,/g, '.')) : num;

    // 2. Verificar si es un número válido
    if (isNaN(number) || number === null || number === undefined) return 0;
    
    // 3. Redondear a dos decimales (ej: 10.555 -> 10.56)
    return Math.round(number * 100) / 100;
};

export const MetodoPago = ({
  pagos,
  setPagos,
  cliente,
  setCliente,
  estado,
  setEstado,
  totalSinDescuento,
  descuentoAplicado,
  setDescuentoAplicado,
  promoDescuento,
  setPromoDescuento,
  motivoPromo,
  setMotivoPromo,
  totalFinalAPagar, // Monto que falta pagar (después de descuentos y NC)
  isNcUsed // ⭐️ NUEVA PROP: Indica si se usó una Nota de Crédito
}) => {

  // Métodos de pago disponibles
  const metodosDisponibles = [
    { value: "efectivo", label: "Efectivo" },
    { value: "tarjeta", label: "Tarjeta de Débito/Crédito" },
    { value: "mercadopago", label: "Mercado Pago" },
    { value: "transferencia", label: "Transferencia Bancaria" },
    { value: "modo", label: "MODO" },
  ];

  // Estados locales para el pago actual que se está agregando
  const [metodoActual, setMetodoActual] = useState("efectivo");
  const [montoActual, setMontoActual] = useState(0); 

  // Controla si el descuento por efectivo es 10% o 0%
  const [efectivoDiscountPercentage, setEfectivoDiscountPercentage] = useState(10);

  // ⭐️ Lógica para calcular el total ya pagado y el saldo pendiente (usando el helper)
  const totalPagado = useMemo(() => {
    return roundToTwoDecimals(pagos.reduce((sum, p) => sum + roundToTwoDecimals(p.monto), 0));
  }, [pagos]);

  // ⭐️ Aplicamos el helper para el saldo pendiente
  const saldoPendiente = useMemo(() => {
    return roundToTwoDecimals(Math.max(0, totalFinalAPagar - totalPagado));
  }, [totalFinalAPagar, totalPagado]);
  

  // 💥 EFECTO CRÍTICO (CORREGIDO): Lógica de Cálculo del Descuento por Efectivo 
  // Esto debe ejecutarse antes de agregar el pago para que el monto a pagar sea NETO.
  useEffect(() => {
    // 1. Si se usó una NC, forzar el descuento a 0 y salir.
    if (isNcUsed) {
      setDescuentoAplicado(0);
      setEfectivoDiscountPercentage(0); 
      return; 
    }
    
    // 2. Determinar si el descuento por efectivo aplica:
    // A. Si no hay pagos registrados, aplica si el método seleccionado es 'efectivo'.
    const isInitialEfectivo = pagos.length === 0 && metodoActual === "efectivo";
    // B. Si ya hay pagos, aplica si todos son 'efectivo' (para el caso de quitar un pago).
    const isOnlyEfectivo = pagos.length > 0 && pagos.every(p => p.metodo === "efectivo");
    
    if (isInitialEfectivo || isOnlyEfectivo) {
      const discountPercent = efectivoDiscountPercentage / 100;
      // El descuento se calcula sobre el total SIN DESCUENTO
      const disc = roundToTwoDecimals(totalSinDescuento * discountPercent); 
      setDescuentoAplicado(disc);
    } else {
      // Si se selecciona otro método o hay pagos combinados, el descuento es 0.
      setDescuentoAplicado(0);
    }
    
  }, [totalSinDescuento, setDescuentoAplicado, efectivoDiscountPercentage, pagos, isNcUsed, metodoActual]);
  
  
  // ⭐️ Inicializar el monto actual al saldo pendiente 
  useEffect(() => {
    // Si el saldo es casi cero, forzar 0. Si no, usar el saldo.
    if (saldoPendiente < 0.01) {
        setMontoActual(0);
    } else {
        setMontoActual(saldoPendiente);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalFinalAPagar, saldoPendiente]);


  // ⭐️ Handler para agregar un pago
  const handleAgregarPago = () => {
    // Aseguramos que el monto sea un número válido y redondeado a 2 decimales
    const monto = roundToTwoDecimals(montoActual); 
    const montoPendiente = saldoPendiente; // Usamos el saldoPendiente calculado

    if (monto <= 0) {
        alert("El monto debe ser un número válido mayor a cero.");
        return;
    }

    if (monto > montoPendiente + 0.005) { // Tolerancia por si el usuario ajusta el valor
        alert(`El monto no puede superar el saldo pendiente: ${formatARS(montoPendiente)}`);
        return;
    }
    
    // 💥 AHORA ESTO ES CLAVE: El monto a pagar es el monto ingresado (NETO)
    // El totalFinalAPagar ya fue ajustado por el useEffect, y montoActual tiene ese valor.
    const montoAPagar = monto; 

    setPagos([...pagos, { metodo: metodoActual, monto: montoAPagar }]);
    
    // Aplicamos redondeo al saldo restante para evitar problemas de coma flotante
    const nuevoSaldo = roundToTwoDecimals(montoPendiente - montoAPagar);
    setMetodoActual("efectivo"); // Se resetea a efectivo para el siguiente pago
    setMontoActual(nuevoSaldo); 
  };
  
  // ⭐️ Handler para quitar un pago
  const handleQuitarPago = (index) => {
    const nuevosPagos = pagos.filter((_, i) => i !== index);
    setPagos(nuevosPagos);
    // El useEffect de arriba recalculará el descuento y saldo.
  };
  
  // ⭐️ Función para mapear el valor del método a un Badge visual
  const getMetodoBadge = (metodo) => {
    switch (metodo) {
        case "efectivo": return <Badge bg="warning" text="dark">Efectivo</Badge>;
        case "tarjeta": return <Badge bg="primary">Tarjeta</Badge>;
        case "mercadopago": return <Badge bg="success">M. Pago</Badge>;
        case "transferencia": return <Badge bg="info">Transferencia</Badge>;
        case "modo": return <Badge bg="secondary">MODO</Badge>;
        default: return <Badge bg="light" text="dark">{metodo}</Badge>;
    }
  };

  // Handler para el input de monto, permite decimales y valida.
  const handleMontoInputChange = (e) => {
    let rawValue = e.target.value;
    
    if (rawValue === "") {
        setMontoActual(0);
        return;
    }

    let sanitizedValue = rawValue.replace(/,/g, "."); 
    
    const isDecimalInProgress = sanitizedValue.endsWith('.') && sanitizedValue.indexOf('.') === sanitizedValue.lastIndexOf('.');

    if (isDecimalInProgress) {
        setMontoActual(sanitizedValue);
        return;
    }
    
    let val = Number(sanitizedValue);

    if (isNaN(val)) {
        return; 
    }
    
    if (val < 0) val = 0;
    
    setMontoActual(val); 
  };
  
  const handleMontoInputBlur = () => {
    let finalVal = roundToTwoDecimals(Number(montoActual));
    
    // Asegurarse de que el monto no exceda el saldo pendiente.
    setMontoActual(roundToTwoDecimals(Math.min(finalVal, saldoPendiente)));
  };


  return (
    <div className="metodo-pago">
      <h5 className="mb-3">Método(s) de Pago</h5>
      
      {/* ⭐️ Resumen de Pagos Agregados */}
      {pagos.length > 0 && (
          <div className="mb-3 p-3 border rounded bg-light">
              <h6 className="fw-bold">Pagos Registrados ({pagos.length})</h6>
              {pagos.map((p, index) => (
                  <div key={index} className="d-flex justify-content-between align-items-center mb-1">
                      <div>
                          {getMetodoBadge(p.metodo)}
                          <span className="ms-2 small text-muted">Monto:</span>
                          <span className="ms-1 fw-bold text-dark">{formatARS(p.monto)}</span>
                      </div>
                      <Button variant="outline-danger" size="sm" onClick={() => handleQuitarPago(index)}>
                          X
                      </Button>
                  </div>
              ))}
              <hr className="my-2" />
              <div className="d-flex justify-content-between fw-bold">
                  <span>Total Pagado:</span>
                  <span className="text-success">{formatARS(totalPagado)}</span>
              </div>
          </div>
      )}

      {/* ⭐️ Formulario para Agregar Nuevo Pago (solo si queda saldo) */}
      {saldoPendiente > 0 && totalFinalAPagar > 0 && (
        <Row className="g-2 mb-3 border p-3 rounded">
            <h6 className="fw-bold text-primary">Agregar Pago</h6>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small">Método</Form.Label>
                <Form.Select
                  value={metodoActual}
                  onChange={(e) => {
                    setMetodoActual(e.target.value);
                    // Inicializa al monto restante al cambiar
                    setMontoActual(saldoPendiente); 
                  }}
                >
                  {metodosDisponibles.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small">Monto a pagar</Form.Label>
                <Form.Control
                  type="text" 
                  value={montoActual.toString()} 
                  onChange={handleMontoInputChange}
                  onBlur={handleMontoInputBlur}
                  placeholder="Monto"
                />
              </Form.Group>
            </Col>
            <Col xs={12} className="mt-3">
                <Button 
                    variant="primary" 
                    onClick={handleAgregarPago} 
                    className="w-100"
                    // Desactivar si el monto no es válido o es mayor al saldo.
                    disabled={roundToTwoDecimals(Number(montoActual)) <= 0 || roundToTwoDecimals(Number(montoActual)) > saldoPendiente}
                >
                    ➕ Agregar Pago
                </Button>
            </Col>
            
            {/* ⭐️ Indicador de Saldo Pendiente */}
            <Col xs={12} className="mt-2 text-end">
                <span className="small text-danger fw-bold">
                    Saldo Pendiente: {formatARS(saldoPendiente)}
                </span>
            </Col>
        </Row>
      )}


      <Row>
        {/* DESCUENTO EN EFECTIVO (%) */}
        <Col md={6} className="mt-2">
          <Form.Group>
            <Form.Label className="small">Descuento Efectivo (%)</Form.Label>
            <Form.Select
              value={efectivoDiscountPercentage}
              onChange={(e) => setEfectivoDiscountPercentage(Number(e.target.value))}
              // ⭐️ Desactivar si no hay pagos en efectivo registrados O si se usó NC
              disabled={!(pagos.some(p => p.metodo === "efectivo") || metodoActual === "efectivo") || isNcUsed} 
            >
              <option value={35}>35%</option>
              <option value={30}>30%</option>
              <option value={25}>25%</option>
              <option value={20}>20%</option>
              <option value={15}>15%</option>
              <option value={10}>10%</option>
              <option value={5}>5%</option>
              <option value={0}>0%</option>
            </Form.Select>
             {/* Mostrar alerta si está desactivado por NC */}
             {isNcUsed && (
                <div className="small text-danger mt-1 fw-bold">
                    Desactivado: Nota de Crédito aplicada.
                </div>
              )}
             {/* Info de descuentos */}
             {descuentoAplicado > 0 && !isNcUsed && ( 
              <div className="small text-muted mt-1">
                Desc. efectivo: {formatARS(descuentoAplicado)} ({efectivoDiscountPercentage}%)
              </div>
            )}
          </Form.Group>
        </Col>

        {/* DESCUENTO PROMO (%) */}
        <Col md={6} className="mt-2">
          <Form.Group>
            <Form.Label className="small">Descuento promoción (%)</Form.Label>
            <Form.Control
              type="number"
              value={promoDescuento}
              onChange={(e) => {
                let val = Number(e.target.value);
                if (val < 0) val = 0;
                if (val > 100) val = 100;
                setPromoDescuento(val);
              }}
              placeholder="0"
            />
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        {/* MOTIVO PROMO */}
        <Col md={12} className="mt-2">
          <Form.Group>
            <Form.Label className="small">Motivo</Form.Label>
            <Form.Control
              type="text"
              value={motivoPromo}
              onChange={(e) => setMotivoPromo(e.target.value)}
              placeholder="Ej: Promo 3x2"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mt-3">
        {/* CLIENTE */}
        <Col md={6}>
          <Form.Group>
            <Form.Label className="small">Cliente (Opcional)</Form.Label>
            <Form.Control
              type="text"
              value={cliente}
              onChange={(e) => setCliente(e.target.value)}
              placeholder="Nombre o ID del cliente"
            />
          </Form.Group>
        </Col>

        {/* ESTADO DE VENTA (Seña/Completada) */}
        <Col md={6}>
          <Form.Group>
            <Form.Label className="small">Estado de Venta</Form.Label>
            <Form.Select
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
            >
              <option value="completada">Completada</option>
              <option value="seña_pendiente">Seña Pendiente</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};